import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Phone, User, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const STORAGE_KEY = "superstate_chat_session";
const LANG_KEY = "superstate_chat_lang";

export default function ChatWidget() {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const pollRef = useRef(null);
  const scrollRef = useRef(null);

  // On open or language change: ensure a session exists that matches current language
  useEffect(() => {
    if (!open) return;
    const existing = localStorage.getItem(STORAGE_KEY);
    const savedLang = localStorage.getItem(LANG_KEY);
    if (existing && savedLang === lang) {
      setSessionId(existing);
      loadMessages(existing);
    } else {
      startSession();
    }
  }, [open, lang]);

  useEffect(() => {
    if (!open || !sessionId) return;
    pollRef.current = setInterval(() => loadMessages(sessionId, true), 5000);
    return () => clearInterval(pollRef.current);
  }, [open, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const startSession = async () => {
    try {
      const { data } = await api.post("/chat/start", { language: lang });
      localStorage.setItem(STORAGE_KEY, data.session_id);
      localStorage.setItem(LANG_KEY, lang);
      setSessionId(data.session_id);
      setMessages([data.message]);
      setShowForm(false);
      setSubmitted(false);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMessages = async (sid, silent = false) => {
    try {
      const { data } = await api.get(`/chat/messages/${sid}`);
      const seen = new Set();
      setMessages(data.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true))));
    } catch (e) {
      if (!silent) console.error(e);
    }
  };

  const sendText = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || !sessionId) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { id: `tmp-${Date.now()}`, role: "user", text, timestamp: new Date().toISOString() },
    ]);
    setTyping(true);
    try {
      const { data } = await api.post("/chat/message", {
        session_id: sessionId,
        text,
        language: lang,
      });
      setMessages((m) => {
        const filtered = m.filter((x) => !String(x.id).startsWith("tmp-"));
        return [...filtered, data.user_message, data.bot_message];
      });
      if (data.request_human) setShowForm(true);
    } catch (e) {
      console.error(e);
      toast.error(t.chat.errorToast);
    } finally {
      setTyping(false);
    }
  };

  const requestHuman = async () => {
    if (!form.name || !form.phone || !form.email) {
      toast.error(t.chat.errorAll);
      return;
    }
    try {
      await api.post("/chat/request-human", {
        session_id: sessionId,
        ...form,
        summary: messages.slice(-6).map((m) => `${m.role}: ${m.text}`).join("\n"),
      });
      setSubmitted(true);
      setShowForm(false);
      toast.success(t.chat.successToast);
    } catch (e) {
      console.error(e);
      toast.error(t.chat.errorGeneric);
    }
  };

  // Map a quick-reply label to an actual user message (always in current language)
  const quickReplyText = (label) => {
    const idx = t.chat.quickReplies.indexOf(label);
    if (idx === 3) {
      // "Call Now" / "Llamar Ahora" — open phone
      window.location.href = "tel:7865438906";
      return null;
    }
    return label;
  };

  return (
    <>
      <button
        data-testid="live-chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 right-6 z-[60] w-14 h-14 md:w-16 md:h-16 rounded-full bg-gold text-ink flex items-center justify-center shadow-2xl hover:scale-110 transition-transform gold-glow-hover gold-glow"
        aria-label="Open live chat"
      >
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>

      {open && (
        <div
          data-testid="live-chat-panel"
          className="fixed bottom-40 right-4 md:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[380px] h-[560px] max-h-[calc(100vh-12rem)] bg-ink-card border border-ink-line rounded-lg shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-2"
        >
          <div className="px-5 py-4 bg-gradient-to-r from-ink to-ink-soft border-b border-ink-line">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center">
                  <MessageSquare size={18} className="text-gold" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full ring-2 ring-ink-card" />
              </div>
              <div>
                <div className="font-heading text-lg text-white leading-none">{t.chat.header}</div>
                <div className="text-xs text-white/60 mt-1">{t.chat.subheader}</div>
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#0d0d1a]">
            {messages.map((m) => (
              <MessageBubble key={m.id} m={m} />
            ))}
            {typing && (
              <div className="flex gap-1.5 px-4 py-3 bg-ink-card rounded-lg w-fit border border-ink-line">
                <span className="typing-dot w-1.5 h-1.5 bg-gold rounded-full" />
                <span className="typing-dot w-1.5 h-1.5 bg-gold rounded-full" />
                <span className="typing-dot w-1.5 h-1.5 bg-gold rounded-full" />
              </div>
            )}

            {/* Quick-reply chips shown when there's only the bot greeting so far */}
            {!typing && !showForm && !submitted && messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {t.chat.quickReplies.map((qr) => (
                  <button
                    key={qr}
                    data-testid={`quick-reply-${qr.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => {
                      const text = quickReplyText(qr);
                      if (text) sendText(text);
                    }}
                    className="px-3 py-1.5 rounded-full border border-gold/40 bg-gold/10 text-gold text-xs font-bold hover:bg-gold hover:text-ink transition-colors"
                  >
                    {qr}
                  </button>
                ))}
              </div>
            )}

            {showForm && (
              <div className="bg-ink-soft border border-gold/40 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-gold text-sm font-bold uppercase tracking-wider">
                  <User size={14} /> {t.chat.humanFormTitle}
                </div>
                <input
                  data-testid="chat-human-name"
                  className="w-full bg-ink border border-ink-line rounded px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                  placeholder={t.chat.humanFormName}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  data-testid="chat-human-phone"
                  className="w-full bg-ink border border-ink-line rounded px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                  placeholder={t.chat.humanFormPhone}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
                <input
                  data-testid="chat-human-email"
                  type="email"
                  className="w-full bg-ink border border-ink-line rounded px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                  placeholder={t.chat.humanFormEmail}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <button
                  data-testid="chat-request-human-submit"
                  onClick={requestHuman}
                  className="w-full bg-gold text-ink font-bold uppercase text-sm tracking-wider py-2.5 rounded hover:bg-gold-hover transition-colors"
                >
                  {t.chat.humanFormSubmit}
                </button>
              </div>
            )}

            {submitted && (
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 text-sm text-white space-y-3">
                <div className="flex gap-3">
                  <CheckCircle2 size={18} className="text-gold shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold">{t.chat.submittedTitle}</div>
                    <div className="text-white/70 text-xs mt-1">{t.chat.submittedDesc}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={`https://wa.me/17865438906?text=${encodeURIComponent(
                      `${t.chat.prefilledMsg} ${form.name}.`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="chat-whatsapp-btn"
                    className="inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded hover:bg-[#1ebe57] transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787" />
                    </svg>
                    {t.chat.whatsapp}
                  </a>
                  <a
                    href="tel:7865438906"
                    className="inline-flex items-center justify-center gap-1.5 bg-ink border border-gold/40 text-gold text-xs font-bold uppercase tracking-wider py-2.5 rounded hover:bg-gold hover:text-ink transition-colors"
                  >
                    <Phone size={12} /> {t.chat.call}
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-ink-line bg-ink-card p-3 space-y-2">
            <div className="flex gap-2">
              <input
                data-testid="chat-input"
                className="flex-1 bg-ink border border-ink-line rounded px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                placeholder={t.chat.inputPlaceholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendText()}
                disabled={typing}
              />
              <button
                data-testid="chat-send"
                onClick={() => sendText()}
                disabled={typing || !input.trim()}
                className="bg-gold text-ink w-10 h-10 rounded flex items-center justify-center hover:bg-gold-hover transition-colors disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
            <a
              href="tel:7865438906"
              className="flex items-center justify-center gap-2 text-xs text-white/60 hover:text-gold transition-colors"
            >
              <Phone size={12} /> {t.chat.callPrompt}
            </a>
          </div>
        </div>
      )}
    </>
  );
}

function MessageBubble({ m }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="bg-gold text-ink px-4 py-2.5 rounded-lg rounded-br-sm max-w-[80%] text-sm leading-relaxed font-medium">
          {m.text}
        </div>
      </div>
    );
  }
  if (m.role === "admin") {
    return (
      <div className="flex justify-start">
        <div className="bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/40 text-white px-4 py-2.5 rounded-lg rounded-bl-sm max-w-[85%] text-sm leading-relaxed">
          <div className="text-[10px] uppercase tracking-widest text-gold font-bold mb-1">Dispatcher</div>
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="bg-ink-card border border-ink-line text-white/90 px-4 py-2.5 rounded-lg rounded-bl-sm max-w-[85%] text-sm leading-relaxed">
        {m.text}
      </div>
    </div>
  );
}
